import { GlobalBus } from '@services/bus'
import { ACTION } from '@constants/Text'
import { STORE_PRINCIPAL as STORE } from '@modules/aawz-intelligence-v2/api/chat-event-handlers'
import { CANAL_RECONFERIR_CONECTORES } from '@modules/aawz-intelligence-v2/constants/Conectores'
import { lerEstadoDosConectores } from '@modules/aawz-intelligence-v2/utils/estado-dos-conectores'
import {
  conectoresPendentes,
  idDaMensagemComConectores,
} from '@modules/aawz-intelligence-v2/utils/conectores-no-thread'

// A VOLTA à tela reconfere os conectores da etapa 3 do onboarding.
//
// ★ Pedido do owner (19/09/2026): *"após conectar e-mail deu tudo certo! Porém
// o card de agenda ali não apareceu conectado... teria como fazer um esquema
// pra, quando voltar pra tela, ele tentar atualizar isso? Inclusive pro
// WhatsApp."* O que o cartão desenha é o retrato do TURNO, e nada o relia.
//
// Três gatilhos, um por caminho de volta:
//
// | caminho | o que acontece | quem dispara |
// |---|---|---|
// | OAuth de e-mail/agenda | o consentimento SAI da página e volta por `returnTo` — a app inteira remonta | o `watch` de `idDaEtapaDeConectores` (`immediate`) |
// | F5 / reabrir a conversa | idem | o mesmo `watch` |
// | WhatsApp pareado | acontece DENTRO da página, sem remontar | `ACTION.ATENDIMENTO_CONEXAO_CONECTADA`, que o modal do QR já emitia |
// | revogação no modal de e-mail/agenda | idem | `CANAL_RECONFERIR_CONECTORES`, **forçado** |
//
// ⚠️ **A chave do `watch` é o ID da mensagem, nunca os estados.** Patchar os
// estados não muda o id, então a releitura dispara UMA vez por conversa aberta;
// observar os estados faria a própria correção pedir outra leitura.
export const viewChatConectoresDoOnboardingMixin = {
  data() {
    return {
      // Uma releitura por vez: os três gatilhos podem coincidir na volta do OAuth.
      reconferindoConectores: false,
    }
  },
  computed: {
    /**
     * O namespace do store DESTA conversa.
     *
     * ⚠️⚠️ **Era `STORE` fixo, e isso fazia a reconferência inteira ser um
     * no-op na Home IA** (achado do owner, 20/09/2026: *"conectei o WhatsApp,
     * deu tudo certo, e o card não atualizou — nem no F5"*; conexão "12",
     * escritório 83). `/home-ia` SEMPRE roda com conversa viva
     * (`view-chat-imersivo.vue::retomarConversasVivas` garante ao menos um id,
     * criando um se não houver), então `view-chat.vue::ns` é
     * `iaConversa:<uuid>` e o módulo `aawz-intelligence-v2` fica VAZIO. Lendo
     * o namespace fixo, `mensagensDoOnboarding` devolvia `[]`,
     * `idDaEtapaDeConectores` virava `null`, o watcher nunca disparava — e o
     * dispatch, se chegasse a rodar, patcharia o slice errado.
     *
     * É a armadilha que o §2 do CLAUDE.md do módulo já nomeia para os FILHOS
     * da view ("quem não recebe o `ns` lê o lugar errado sem erro nenhum") —
     * ela vale igual para um MIXIN dela.
     *
     * O fallback existe para o host mínimo dos testes e para qualquer
     * superfície sem conversa viva (popup, painel do Hub), onde `ns` é o
     * próprio `STORE`.
     */
    nsDosConectores() {
      return this.ns || STORE
    },
    mensagensDoOnboarding() {
      return this.$store.getters[`${this.nsDosConectores}/messages`] || []
    },
    idDaEtapaDeConectores() {
      return idDaMensagemComConectores(this.mensagensDoOnboarding)
    },
  },
  watch: {
    idDaEtapaDeConectores: {
      immediate: true,
      handler(id) {
        if (id) this.reconferirConectoresDoOnboarding()
      },
    },
  },
  created() {
    GlobalBus.$on(
      ACTION.ATENDIMENTO_CONEXAO_CONECTADA,
      this.reconferirConectoresDoOnboarding
    )
    GlobalBus.$on(
      CANAL_RECONFERIR_CONECTORES,
      this.reconferirConectoresRevogados
    )
  },
  beforeDestroy() {
    GlobalBus.$off(
      ACTION.ATENDIMENTO_CONEXAO_CONECTADA,
      this.reconferirConectoresDoOnboarding
    )
    GlobalBus.$off(
      CANAL_RECONFERIR_CONECTORES,
      this.reconferirConectoresRevogados
    )
  },
  methods: {
    /**
     * O gatilho da REVOGAÇÃO — o único que pula o guard de pendência.
     *
     * ⚠️ **Sem o `forcado` este canal era código morto**, e o sintoma era o pior
     * possível: `conectoresPendentes` descarta tudo o que está `conectado`, então
     * com os dois conectores ligados — que é exatamente o estado de quem acaba de
     * revogar um — a lista saía vazia, o ouvinte voltava no `return` e o cartão
     * seguia dizendo "Conectado" sobre um acesso que já não existe. A transição
     * que este canal existe para cobrir era a única que ele nunca cobria.
     *
     * ★ É método PRÓPRIO, não o payload `{forcado: true}` no `$emit`: o handler
     * do outro canal (`ACTION.ATENDIMENTO_CONEXAO_CONECTADA`) e o do `watch`
     * chegam com argumentos que não são opções, e lê-los como tal faria a
     * próxima carga do bus decidir se o guard vale. A referência é estável por
     * instância, então o `$off` do `beforeDestroy` continua casando.
     */
    reconferirConectoresRevogados() {
      return this.reconferirConectoresDoOnboarding({ forcado: true })
    },
    /**
     * Relê o estado real dos conectores e patcha o cartão — se houver o que
     * reconferir.
     *
     * ⚠️ O guard de pendência é lido NA HORA, não é a chave do `watch`: thread
     * com tudo já conectado não gasta request, e o cartão histórico com os dois
     * ligados nunca dispara nada. `forcado` pula SÓ ele — a trava de leitura
     * única fica, senão a volta do OAuth (onde os três gatilhos coincidem)
     * dispararia leituras concorrentes.
     *
     * ⚠️ Falha é SILENCIOSA: ninguém pediu esta leitura, e o cartão continua
     * mostrando o retrato do turno — que é o dado honesto sem leitura nova.
     *
     * @param {Object} [opcoes]
     * @param {Boolean} [opcoes.forcado] Relê mesmo sem conector pendente.
     */
    async reconferirConectoresDoOnboarding({ forcado = false } = {}) {
      if (this.reconferindoConectores) return
      if (!this.idDaEtapaDeConectores) return
      if (!forcado && !conectoresPendentes(this.mensagensDoOnboarding).length) {
        return
      }
      const usuario = this.$currentUser() || {}
      this.reconferindoConectores = true
      try {
        const estados = await lerEstadoDosConectores({
          idProfissional: usuario.id_interno,
        })
        this.$store.dispatch(
          `${this.nsDosConectores}/atualizarEstadoDosConectores`,
          estados
        )
      } finally {
        this.reconferindoConectores = false
      }
    },
  },
}
